import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { qk } from "../api/queries";
import type { LiveEvent } from "../api/types";

function handle(qc: QueryClient, ev: LiveEvent) {
  const { project_id: pid, task_id: tid, run_id: rid } = ev;
  switch (ev.type) {
    case "task":
      if (pid) qc.invalidateQueries({ queryKey: qk.tasks(pid) });
      else qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === "project" && q.queryKey[2] === "tasks" });
      if (tid) {
        qc.invalidateQueries({ queryKey: qk.task(tid) }); // also timeline/diff/env (prefix)
      }
      if (pid) qc.invalidateQueries({ queryKey: qk.usage(pid) });
      qc.invalidateQueries({ queryKey: ["monitor"] });
      break;
    case "timeline":
      if (tid) {
        qc.invalidateQueries({ queryKey: qk.timeline(tid) });
        qc.invalidateQueries({ queryKey: qk.task(tid), exact: true });
        qc.invalidateQueries({ queryKey: qk.diff(tid) });
      }
      break;
    case "run":
      if (rid) qc.invalidateQueries({ queryKey: qk.run(rid) });
      if (tid) qc.invalidateQueries({ queryKey: qk.task(tid), exact: true });
      qc.invalidateQueries({ queryKey: qk.agentPool });
      qc.invalidateQueries({ queryKey: ["monitor"] });
      break;
    case "environment":
      qc.invalidateQueries({ queryKey: ["monitor", "environments"] });
      if (ev.env_id) qc.invalidateQueries({ queryKey: qk.monitorEnvLogs(ev.env_id) });
      if (tid) qc.invalidateQueries({ queryKey: qk.environment(tid) });
      break;
    case "notification":
      qc.invalidateQueries({ queryKey: qk.notifications });
      break;
    case "proposal":
      if (pid) qc.invalidateQueries({ queryKey: qk.proposals(pid) });
      else qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === "project" && q.queryKey[2] === "proposals" });
      break;
    case "project":
      qc.invalidateQueries({ queryKey: qk.projects });
      if (pid) qc.invalidateQueries({ queryKey: ["project", pid] });
      break;
  }
}

/** One EventSource on /api/events; events are cache-invalidation hints. */
export function useLiveEvents(enabled: boolean) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!enabled) return;
    let es: EventSource | null = null;
    let retry: number | undefined;
    let delay = 1000;
    let hadError = false;
    let closed = false;

    const connect = () => {
      es = new EventSource("/api/events", { withCredentials: true });
      es.onopen = () => {
        delay = 1000;
        // We may have missed events while disconnected: refetch everything active.
        if (hadError) qc.invalidateQueries();
        hadError = false;
      };
      es.onmessage = (m) => {
        try {
          handle(qc, JSON.parse(m.data) as LiveEvent);
        } catch {
          /* ignore malformed */
        }
      };
      es.onerror = () => {
        hadError = true;
        if (es?.readyState === EventSource.CLOSED && !closed) {
          es.close();
          retry = window.setTimeout(connect, delay);
          delay = Math.min(delay * 2, 30_000);
        }
      };
    };
    connect();
    return () => {
      closed = true;
      es?.close();
      window.clearTimeout(retry);
    };
  }, [enabled, qc]);
}
